import { useQuery } from '@tanstack/react-query';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { Banknote, Coins, Car, Gauge } from 'lucide-react';
import { api } from '../../lib/api';
import { formatMoney } from '../../lib/utils';
import {
  KpiCard, KpiRow, ChartCard, TableCard, TabLoading, TabError, TabEmpty, CHART, ANIM,
  TOOLTIP_STYLE, TOOLTIP_LABEL_STYLE, TOOLTIP_CURSOR, LINE_CURSOR, GRID_STROKE, AXIS_PROPS, LEGEND_PROPS,
  euroTick, euroFull, paddedDomain,
} from './StatsShared';

const uren2 = (n: number) => n.toFixed(2).replace('.', ',');

export function LoonTab({ year }: { year: number }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['stats-loon', year],
    queryFn: () => api.stats.loon(year),
  });

  if (isLoading) return <TabLoading />;
  if (error || !data) return <TabError error={error} />;

  if (data.periods.length === 0) {
    return <TabEmpty title={`Nog geen loonstroken van ${year}`} text="Zodra er een loonstrook binnen is, verschijnen hier de cijfers." />;
  }

  const bruisData = data.periods.map((p) => ({
    name: `P${p.period}`,
    Netto: Math.round(p.netto * 100) / 100,
    Inhoudingen: Math.round((p.bruto - p.netto + p.reiskosten) * 100) / 100,
    Reiskosten: Math.round(p.reiskosten * 100) / 100,
    totaalBruto: p.bruto,
  }));

  const gemData = data.periods.map((p) => ({
    name: `P${p.period}`,
    Gemiddeld: Math.round(p.gem_per_uur * 100) / 100,
    Basis: 17.74,
  }));

  const cumulativeData = data.cumulative.map((c) => ({
    name: `P${c.period}`,
    [`${year}`]: c.curr_cum,
    [`${year - 1}`]: c.prev_cum,
  }));

  const eur = (v: number) => formatMoney(v);

  return (
    <div className="space-y-4 sm:space-y-6">
      <KpiRow>
        <KpiCard icon={Coins} label="Bruto dit jaar" kpi={data.kpis.bruto} fmt="money" prevYear={year - 1} />
        <KpiCard icon={Banknote} label="Netto uitbetaald" kpi={data.kpis.netto} fmt="money" prevYear={year - 1} />
        <KpiCard icon={Car} label="Reiskosten (onbelast)" kpi={data.kpis.reiskosten} fmt="money" prevYear={year - 1} />
        <KpiCard icon={Gauge} label="Gemiddeld per uur" kpi={data.kpis.gem_per_uur} fmt="eur_per_hour" prevYear={year - 1} />
      </KpiRow>

      <ChartCard title="Waar je loon heen gaat" subtitle="Per periode: netto, inhoudingen en onbelaste reiskosten">
        <ResponsiveContainer>
          <BarChart data={bruisData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid stroke={GRID_STROKE} vertical={false} />
            <XAxis dataKey="name" {...AXIS_PROPS} />
            <YAxis {...AXIS_PROPS} tickFormatter={euroTick} width={52} />
            <Tooltip contentStyle={TOOLTIP_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} cursor={TOOLTIP_CURSOR} formatter={(v: number) => eur(v)} />
            <Legend {...LEGEND_PROPS} />
            <Bar dataKey="Netto" stackId="a" fill={CHART.money} isAnimationActive={false} />
            <Bar dataKey="Inhoudingen" stackId="a" fill={CHART.muted} isAnimationActive={false} />
            <Bar dataKey="Reiskosten" stackId="a" fill={CHART.main} radius={[4, 4, 0, 0]} isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <div className="grid gap-4 sm:gap-6 lg:grid-cols-2 [&>*]:min-w-0">
        <ChartCard title="Bruto opgeteld" subtitle={`${year} naast ${year - 1}`} height={240}>
          <ResponsiveContainer>
            <LineChart data={cumulativeData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid stroke={GRID_STROKE} vertical={false} />
              <XAxis dataKey="name" {...AXIS_PROPS} />
              <YAxis {...AXIS_PROPS} tickFormatter={euroTick} width={52} />
              <Tooltip contentStyle={TOOLTIP_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} cursor={LINE_CURSOR} formatter={(v: unknown) => euroFull(v)} />
              <Legend {...LEGEND_PROPS} />
              <Line type="monotone" dataKey={`${year}`} stroke={CHART.main} strokeWidth={2} dot={{ r: 2.5, fill: CHART.main }} connectNulls isAnimationActive={false} />
              <Line type="monotone" dataKey={`${year - 1}`} stroke={CHART.muted} strokeWidth={2} strokeDasharray="4 3" dot={false} connectNulls isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Gemiddeld uurloon" subtitle="Met toeslagen, naast het CAO-basisuurloon" height={240}>
          <ResponsiveContainer>
            <LineChart data={gemData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid stroke={GRID_STROKE} vertical={false} />
              <XAxis dataKey="name" {...AXIS_PROPS} />
              <YAxis {...AXIS_PROPS} domain={paddedDomain(1)} tickFormatter={(v: number) => `€${v}`} width={44} />
              <Tooltip contentStyle={TOOLTIP_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} cursor={LINE_CURSOR} formatter={(v: number) => eur(v)} />
              <Legend {...LEGEND_PROPS} />
              <Line type="monotone" dataKey="Gemiddeld" stroke={CHART.main} strokeWidth={2} dot={{ r: 2.5, fill: CHART.main }} isAnimationActive={false} />
              <Line type="monotone" dataKey="Basis" name="CAO-basis" stroke={CHART.muted} strokeDasharray="4 3" dot={false} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <TableCard title="Per periode">
        <table className="table whitespace-nowrap">
          <thead>
            <tr>
              <th>Periode</th>
              <th className="r">Uren</th>
              <th className="r">Bruto</th>
              <th className="r">Loonheffing</th>
              <th className="r">Pensioen</th>
              <th className="r">Premies</th>
              <th className="r">Reiskosten</th>
              <th className="r">Netto</th>
            </tr>
          </thead>
          <tbody>
            {data.periods.map((p) => (
              <tr key={p.period}>
                <td className="text-slate-300">P{p.period}</td>
                <td className="r">{uren2(p.uren)}</td>
                <td className="r">{eur(p.bruto)}</td>
                <td className="r text-slate-400">{eur(p.inhoudingen.loonheffing)}</td>
                <td className="r text-slate-400">{eur(p.inhoudingen.pensioen)}</td>
                <td className="r text-slate-400">{eur(p.inhoudingen.premie_ww + p.inhoudingen.fba + p.inhoudingen.overig)}</td>
                <td className="r">{eur(p.reiskosten)}</td>
                <td className="r font-medium text-money">{eur(p.netto)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="font-semibold text-slate-100">
            <tr>
              <td>Totaal</td>
              <td className="r">{uren2(data.totals.uren)}</td>
              <td className="r">{eur(data.totals.bruto)}</td>
              <td className="r">{eur(data.totals.loonheffing)}</td>
              <td colSpan={2}></td>
              <td className="r">{eur(data.totals.reiskosten)}</td>
              <td className="r text-money">{eur(data.totals.netto)}</td>
            </tr>
          </tfoot>
        </table>
      </TableCard>
    </div>
  );
}
